/**
 * Alerts' adapter onto the firebase module (frontend-live-contract, 2026-07-20).
 *
 * Firestore is a best-effort wake-up/sound HINT channel only — never canonical
 * Alert state. Canonical current work is always `GET /api/alerts/queue/`.
 *
 * The firebase module owns the transport (named app, in-memory auth, serial
 * sign-in chain, silent baseline, dedup). This file owns what an alert hint IS:
 * the wire schema and its identity invariant.
 */
import { startLiveCollectionListener } from "@/firebase";
import type { LiveConnectionState, LiveListenerHandle } from "@/firebase";

/** Dedicated app — must NOT reuse the default Firebase Auth used by other features. */
const ALERT_LIVE_APP_NAME = "minemaestro-alert-live";

/** Validated Firestore hint. IDs that map to SQL bigints are STRINGS by contract. */
export interface AlertLiveEvent {
  schema_version: 1;
  topic: "alert.activity.appended.v1";
  event_uuid: string;
  alert_id: string;
  machine_id: string | null;
  device_mxid: string;
  activity_id: string;
  activity_uuid: string;
  activity_seq: number;
  activity_kind: string;
  review_cycle: number;
  status_before: string;
  status_after: string;
  signal: "attention" | "cleared" | "info";
}

export type AlertLiveConnectionState = LiveConnectionState;
export type AlertLiveHandle = LiveListenerHandle;

export interface StartAlertLiveArgs {
  customToken: string;
  /** Web-config projectId MUST equal this or we never sign in / listen. */
  expectedProjectId: string;
  collectionPath: string;
  /** Generation guard: return true once this transition has been superseded. */
  isStale: () => boolean;
  /** Fires once, after the first server-confirmed (non-cache) baseline snapshot. */
  onBaselineComplete: () => void;
  onConnectionState: (state: AlertLiveConnectionState) => void;
  /** A fresh, validated, deduplicated `added` hint (never during the baseline). */
  onHint: (event: AlertLiveEvent) => void;
  /** Listener/auth error — caller must enter REST-only fallback. */
  onError: (error: Error) => void;
}

export function validateAlertLiveEvent(docId: string, data: unknown): AlertLiveEvent | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;

  if (d.schema_version !== 1) return null;
  if (d.topic !== "alert.activity.appended.v1") return null;

  const str = (v: unknown): string | null =>
    typeof v === "string" && v.length > 0 ? v : null;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;

  const event_uuid = str(d.event_uuid);
  const activity_uuid = str(d.activity_uuid);
  const alert_id = str(d.alert_id);
  const activity_id = str(d.activity_id);
  const device_mxid = str(d.device_mxid);
  const activity_kind = str(d.activity_kind);
  const status_before = str(d.status_before);
  const status_after = str(d.status_after);

  if (
    !event_uuid ||
    !activity_uuid ||
    !alert_id ||
    !activity_id ||
    !device_mxid ||
    !activity_kind ||
    !status_before ||
    !status_after
  ) {
    return null;
  }

  // Immutable-identity invariant: the doc id, event uuid, and activity uuid
  // must all be the same value (Celery retries overwrite the same document).
  if (docId !== event_uuid || event_uuid !== activity_uuid) return null;

  // machine_id is nullable; when present it must be a string (never a number).
  if (d.machine_id != null && typeof d.machine_id !== "string") return null;
  const machine_id = typeof d.machine_id === "string" ? d.machine_id : null;

  const activity_seq = num(d.activity_seq);
  const review_cycle = num(d.review_cycle);
  if (activity_seq == null || review_cycle == null) return null;

  const signal = d.signal;
  if (signal !== "attention" && signal !== "cleared" && signal !== "info") return null;

  return {
    schema_version: 1,
    topic: "alert.activity.appended.v1",
    event_uuid,
    alert_id,
    machine_id,
    device_mxid,
    activity_id,
    activity_uuid,
    activity_seq,
    activity_kind,
    review_cycle,
    status_before,
    status_after,
    signal,
  };
}

/**
 * Start (or replace) the alert-live listener for one organization/location.
 *
 * Returns a handle whose `stop()` unsubscribes and signs the live session out,
 * or `null` if the transition was superseded before the listener attached.
 */
export function startAlertLiveListener(
  args: StartAlertLiveArgs,
): Promise<AlertLiveHandle | null> {
  return startLiveCollectionListener<AlertLiveEvent>({
    appName: ALERT_LIVE_APP_NAME,
    customToken: args.customToken,
    expectedProjectId: args.expectedProjectId,
    collectionPath: args.collectionPath,
    validate: validateAlertLiveEvent,
    isStale: args.isStale,
    onBaselineComplete: args.onBaselineComplete,
    onConnectionState: args.onConnectionState,
    onHint: args.onHint,
    onError: args.onError,
  });
}
